import { create } from "zustand";
import { tabMetaForPath, TAB_SKIP_PATHS } from "./nav";

/**
 * UYGULAMA İÇİ SEKME DEPOSU.
 *
 * ── Sekme kimliği = YOL (pathname) ──
 * `/items/stock` ile `/items/semi` aynı bileşeni render eder ama AYRI sekmelerdir.
 * Kimliği yola bağlamak, "aynı sayfa iki kez açılmasın" kuralını da bedavaya getirir:
 * zaten açık bir yola gidilirse yeni sekme açılmaz, var olan öne gelir.
 *
 * ── Neden sessionStorage, localStorage DEĞİL ──
 * Depoda `storage` olayı dinleyen ya da BroadcastChannel kullanan hiçbir yer yok. Sekme
 * listesi localStorage'a yazılsaydı, uygulamayı İKİ TARAYICI SEKMESİNDE açan kullanıcının
 * pencereleri birbirinin listesini sessizce ezerdi. sessionStorage tarayıcı-sekmesi
 * başına izoledir — doğru kap budur.
 *
 * ── Yenilemeden sonra ne kalır ──
 * Yalnız LİSTE (hangi yollar açık) kalır; sayfa içeriği ve yarım formlar KALMAZ — onlar
 * JS belleğinde yaşıyordu ve yenileme onu zaten siler. Kullanıcıya söz verilen şey de bu.
 */

const KEY = "steperp.tabs.v1";

/** Kullanıcı kararı: en fazla 10 sekme; dolunca en eski KULLANILAN sekme kapanır. */
export const MAX_TABS = 10;

export interface TabItem {
  /** Kimlik = pathname. */
  id: string;
  path: string;
  title: string;
  icon: string;
  /** Son öne getirilme zamanı — sınır dolunca en eskisi kapanır (LRU). */
  usedAt: number;
}

interface Persisted {
  tabs: Array<Pick<TabItem, "id" | "path" | "title" | "icon">>;
  activeId: string | null;
}

function load(): Persisted | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as Persisted;
    if (!Array.isArray(p.tabs)) return null;
    // Yenileme sonrası menü kataloğu değişmiş olabilir (deploy) — başlıkları TAZELE.
    return {
      tabs: p.tabs
        .filter((t) => typeof t?.path === "string")
        .slice(0, MAX_TABS)
        .map((t) => ({ id: t.path, path: t.path, ...tabMetaForPath(t.path) })),
      activeId: typeof p.activeId === "string" ? p.activeId : null,
    };
  } catch {
    return null; // bozuk JSON sekme sistemini kilitlemesin
  }
}

function persist(tabs: TabItem[], activeId: string | null): void {
  try {
    const data: Persisted = {
      tabs: tabs.map((t) => ({ id: t.id, path: t.path, title: t.title, icon: t.icon })),
      activeId,
    };
    sessionStorage.setItem(KEY, JSON.stringify(data));
  } catch {
    /* kota dolu / gizli mod — sekmeler bellekte çalışmaya devam eder */
  }
}

interface TabState {
  tabs: TabItem[];
  activeId: string | null;
  /** Yolu aç ya da zaten açıksa öne getir. */
  open: (path: string) => void;
  /** Sekmeyi kapat; dönen değer gidilmesi gereken yol (null = kapatılan aktif değildi). */
  close: (id: string) => string | null;
  closeOthers: (id: string) => void;
  closeAll: () => void;
  /**
   * TESİS DEĞİŞTİ — açık sekmeleri sıfırla.
   *
   * Sekme yolları tesisten bağımsızdır ama sekmelerin İÇİNDEKİ durum değildir: seçili
   * lokasyon, seçili lot, seçili üretim merkezi hep ESKİ tesisin kayıtlarıdır. `invalidateQueries`
   * yalnız sunucu verisini tazeler, bileşen state'ine dokunmaz — o seçimler ekranda kalır ve
   * kullanıcı farkında olmadan ÇAPRAZ TESİS işlemi yapabilir. Bu, veri izolasyonunun
   * sessizce delinmesi demektir.
   *
   * Bu yüzden: aktif sekme DIŞINDAKİLER kapanır ve `gen` artırılarak kalan sekme de
   * yeniden mount edilir (tüm iç state sıfırlanır).
   */
  resetForFacility: () => void;
  /** Panel `key`ine girer; artması tüm sekmeleri yeniden mount ettirir. */
  gen: number;
}

const now = () => Date.now();

const initial = load();

export const useTabStore = create<TabState>((set, get) => ({
  tabs: (initial?.tabs ?? []).map((t) => ({ ...t, usedAt: now() })),
  activeId: initial?.activeId ?? null,

  open: (path) => {
    // Yönlendirme yolları ve katalogda olmayan adresler sekme AÇMAZ; gezinme yine çalışır.
    // Aksi hâlde `/formlar` gibi bir yönlendirme ölü bir sekme bırakır, bozuk bir derin
    // bağlantı da `<Navigate>` render eden hayalet sekme yaratırdı.
    if (TAB_SKIP_PATHS.has(path)) return;
    if (!tabMetaForPath(path).pageKey) return;
    const { tabs } = get();
    const existing = tabs.find((t) => t.id === path);
    if (existing) {
      // Zaten açık: yeni sekme AÇMA, öne getir. Kullanıcının o sekmedeki durumu korunur.
      const next = tabs.map((t) => (t.id === path ? { ...t, usedAt: now() } : t));
      set({ tabs: next, activeId: path });
      persist(next, path);
      return;
    }
    const meta = tabMetaForPath(path);
    let next = [...tabs, { id: path, path, title: meta.title, icon: meta.icon, usedAt: now() }];
    if (next.length > MAX_TABS) {
      // Sınır aşıldı: EN ESKİ KULLANILAN sekme kapanır. Aktif olan asla kurban seçilmez —
      // kullanıcının o an baktığı sekmeyi kapatmak en beklenmedik davranış olurdu.
      const victim = next
        .filter((t) => t.id !== path)
        .reduce((a, b) => (a.usedAt <= b.usedAt ? a : b));
      next = next.filter((t) => t.id !== victim.id);
    }
    set({ tabs: next, activeId: path });
    persist(next, path);
  },

  close: (id) => {
    const { tabs, activeId } = get();
    const idx = tabs.findIndex((t) => t.id === id);
    if (idx === -1) return null;
    const next = tabs.filter((t) => t.id !== id);
    if (activeId !== id) {
      set({ tabs: next });
      persist(next, activeId);
      return null;
    }
    // Aktif sekme kapandı: KOMŞUYA geç (sağdaki, yoksa soldaki). Hiç kalmadıysa Dashboard.
    const neighbour = next[idx] ?? next[idx - 1] ?? null;
    const target = neighbour?.path ?? "/dashboard";
    set({ tabs: next, activeId: neighbour?.id ?? null });
    persist(next, neighbour?.id ?? null);
    return target;
  },

  closeOthers: (id) => {
    const { tabs } = get();
    const keep = tabs.filter((t) => t.id === id);
    set({ tabs: keep, activeId: keep[0]?.id ?? null });
    persist(keep, keep[0]?.id ?? null);
  },

  closeAll: () => {
    set({ tabs: [], activeId: null });
    persist([], null);
  },

  gen: 0,
  resetForFacility: () => {
    const { tabs, activeId, gen } = get();
    const keep = tabs.filter((t) => t.id === activeId);
    set({ tabs: keep, activeId: keep[0]?.id ?? null, gen: gen + 1 });
    persist(keep, keep[0]?.id ?? null);
  },
}));
